// Core packages

// Third party packages

// Custom packages
import {
  PropertyRemappingItem,
  RemappingAuthor,
} from "src/app/shared/models/property/property-remapping.interface";

/**
 * The only `propertyStatus` fields read to resolve the mapping info of the
 * property home. Every level is optional: properties created before a status
 * field existed do not carry it, and `mappingCompleted.user` may still be a raw
 * id when the back-end did not populate it.
 *
 * @since 3.0.0
 */
export interface MappingInfoStatusSource {
  remapping?: {
    user?: string;
    date?: Date | string;
  } | null;
  mappingCompleted?: {
    user?: { firstName?: string; lastName?: string } | string | null;
    date?: Date | string;
  } | null;
}

/**
 * "Mappatore" and "Ultima modifica alla mappatura" of the property home.
 *
 * @since 3.0.0
 */
export interface MappingInfo {
  /** Empty string when no source has a mapper: the page shows "Non presente" */
  mapper: string;
  /** null when no source has a date: the page leaves the value blank */
  lastEditDate: Date | string | null;
}

/**
 * Composes the "Mappatore" label of a remapping record from its
 * server-resolved author, exactly as the "Mappatore" column of the
 * "Elenco rimappature" table shows it.
 *
 * Returns an empty string when the author is not resolved (`createdBy: null`
 * on legacy records or records whose user no longer exists). Either name part
 * may be missing or empty, so both are checked before joining to avoid a
 * dangling separator.
 *
 * @since 3.0.0
 *
 * @param author `createdBy` of the remapping record
 */
export function formatRemappingAuthorName(
  author: RemappingAuthor | null | undefined
): string {
  if (!author) {
    return "";
  }
  const firstName = author.firstName ? author.firstName.trim() : "";
  const lastName = author.lastName ? author.lastName.trim() : "";
  const parts: string[] = [];
  if (firstName) {
    parts.push(firstName);
  }
  if (lastName) {
    parts.push(lastName);
  }
  return parts.join(" ");
}

/**
 * Resolves "Mappatore" and "Ultima modifica alla mappatura" of the property
 * home. The two values follow the same source order but are resolved
 * independently: each one falls through to the next source when the current
 * source does not carry it.
 *
 * 1. RIMAPPATURA of the "Stato" panel (`propertyStatus.remapping`), typed by
 *    the operator: free-text `user` and `date`.
 * 2. Most recent record of the "Elenco rimappature" table (first row with the
 *    table default sort, `date` desc): author of the record (`createdBy`) and
 *    `date`.
 * 3. First closure of the property mapping (`propertyStatus.mappingCompleted`),
 *    written by the back-end: populated `user` (first AND last name required)
 *    and `date`.
 *
 * @since 3.0.0
 *
 * @param propertyStatus `propertyStatus` of GET properties/:id; null and undefined skip levels 1 and 3
 * @param latestRemapping first row of GET property-remappings/:propertyId sorted by date desc, or null when there is none or it could not be read
 */
export function resolveMappingInfo(
  propertyStatus: MappingInfoStatusSource | null | undefined,
  latestRemapping: PropertyRemappingItem | null
): MappingInfo {
  const remapping = propertyStatus ? propertyStatus.remapping : null;
  const mappingCompleted = propertyStatus
    ? propertyStatus.mappingCompleted
    : null;

  let mapper = "";
  const latestRemappingMapper = latestRemapping
    ? formatRemappingAuthorName(latestRemapping.createdBy)
    : "";
  if (remapping && remapping.user) {
    mapper = `${remapping.user}`;
  } else if (latestRemappingMapper) {
    mapper = latestRemappingMapper;
  } else if (
    mappingCompleted &&
    mappingCompleted.user &&
    typeof mappingCompleted.user === "object" &&
    mappingCompleted.user.firstName &&
    mappingCompleted.user.lastName
  ) {
    mapper = `${mappingCompleted.user.firstName} ${mappingCompleted.user.lastName}`;
  }

  let lastEditDate: Date | string | null = null;
  if (remapping && remapping.date) {
    lastEditDate = remapping.date;
  } else if (latestRemapping && latestRemapping.date) {
    lastEditDate = latestRemapping.date;
  } else if (mappingCompleted && mappingCompleted.date) {
    lastEditDate = mappingCompleted.date;
  }

  return { mapper, lastEditDate };
}
